import { yupResolver } from "@hookform/resolvers/yup";
import { TypographyProps } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Column, MuiBox, MuiTable, RowProps, STATUS_BADGE_TYPE } from "../..";
import { useUpdateRevenueSplitForProgramInvoiceByInvoiceId } from "../../../api/services/program/programApis";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import { BE_ERROR_CODES, PAGE_IDS } from "../../../helpers/constants";
import {
  getParsedErrorCode,
  getValueOrBlank,
  isNotEmpty,
} from "../../../helpers/utils";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { manageRevenueSplitSiteCoreKeys } from "../../../pages/createProgram/siteCore/SiteCoreKeys";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import MuiEllipseLabel from "../../ellipseLabel/MuiEllipseLabel";
import { InlineEditPopup } from "../../inlineEditPopup/InlineEditPopup";
import { valueWithoutFormat } from "../../textFieldCurrency/CurrencyUtils";
import {
  COUNSELLING_EDITABLE_RELATIONSHIP_TYPES_ARRAY,
  CounsellingEditableRelationshipType,
  MANAGE_REVENUE_SPLIT_INLINE_FORM_KEYS,
  MANAGE_REVENUE_SPLIT_KEYS,
  MANAGE_REVENUE_SPLIT_ROW_ORDER,
} from "../modelsAndSchemas/models";
import { ManageRevenueSplitInlineValidationSchema } from "../modelsAndSchemas/ValidationSchema";
import { ManageRevenueSplitTableWrapper } from "./ManageRevenueSplitTableWrapper";

export interface ManageRevenueSplitRow {
  id: number;
  officeAffiliate: string;
  officeId?: string;
  sales?: string;
  counseling?: string;
  counselingAmountValue?: string;
  affiliateRoyalty?: string;
  royaltyRetention?: string;
  total?: string;
  relationShipType: CounsellingEditableRelationshipType;
  [key: string]: any;
}

interface ManageRevenueSplitTableProps {
  filteredResults: ManageRevenueSplitRow[];
  invoiceId: string;
  currencyLocale: string;
  addRevenueSplit?: () => void;
  onUpdateRevenueSplit?: (invoiceId: string) => void;
  onDeleteRevenueSplit?: (row: ManageRevenueSplitRow) => void;
  onAddedCallback?: boolean;
  onDeletedCallback?: boolean;
  isViewOnly?: boolean;
  invoiceStatus?: string;
  isInvoiceReversed?: boolean;
  isForProject: boolean;
}

const ManageRevenueSplitTable: React.FC<ManageRevenueSplitTableProps> = ({
  filteredResults,
  invoiceId,
  onAddedCallback,
  onDeletedCallback,
  isViewOnly,
  invoiceStatus,
  isInvoiceReversed,
  currencyLocale,
  onUpdateRevenueSplit,
  isForProject,
}) => {
  const { commonLabels, fetchLabel, commonValidationMessages } = useSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const [showInlinePopup, setShowInlinePopup] = useState({
    officeId: undefined as string | undefined,
    anchorEl: null as HTMLButtonElement | null,
  });

  const [filteredResultsLocal, setFilteredResultsLocal] = useState<
    ManageRevenueSplitRow[]
  >([...filteredResults]);

  const isInvoiceStatusReversed = useMemo(
    () => isInvoiceReversed || invoiceStatus === STATUS_BADGE_TYPE.reversed,
    [isInvoiceReversed, invoiceStatus],
  );

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const pageId = PAGE_IDS.MANAGE_REVENUE_SPLIT;

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [MANAGE_REVENUE_SPLIT_INLINE_FORM_KEYS.COUNSELING]: "",
    },
    resolver: yupResolver(
      ManageRevenueSplitInlineValidationSchema(commonValidationMessages),
    ),
  });

  const { setValue, setError } = methods;

  const {
    isPending: isUpdateRevenueSplitPending,
    isError: isUpdateRevenueSplitError,
    error: updateRevenueSplitError,
    isSuccess: isUpdateRevenueSplitSuccess,
    mutateAsync: updateRevenueSplitAmount,
  } = useUpdateRevenueSplitForProgramInvoiceByInvoiceId();

  useEffect(() => {
    dispatch(
      toggleLoader({
        ManageUpdateRevenueSplitLoading: {
          isVisible: isUpdateRevenueSplitPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          ManageUpdateRevenueSplitLoading: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isUpdateRevenueSplitPending]);

  const showInlineError = (errorMessage: string) => {
    setError(MANAGE_REVENUE_SPLIT_INLINE_FORM_KEYS.COUNSELING, {
      type: "custom",
      message: errorMessage,
    });
  };

  useEffect(() => {
    if (isUpdateRevenueSplitError && isNotEmpty(updateRevenueSplitError)) {
      const errorCode = getParsedErrorCode(updateRevenueSplitError);

      switch (errorCode) {
        case BE_ERROR_CODES.INVALID_COUNSELING_AMOUNT:
          showInlineError(getLabel("counsellingFeeValidationMessage"));
          break;
        case BE_ERROR_CODES.COUNSELING_AMOUNT_MUST_BE_GREATER_THAN_ZERO:
          showInlineError(getLabel("positiveValuesMessage"));
          break;

        default:
          displayToast(
            commonLabels.someThingWentWrongPleaseTryAgainLater,
            "error",
          );
          break;
      }
    }
  }, [isUpdateRevenueSplitError, updateRevenueSplitError]);

  useEffect(() => {
    if (isUpdateRevenueSplitSuccess) {
      onUpdateRevenueSplit?.(invoiceId);
      setShowInlinePopup({
        officeId: undefined,
        anchorEl: null,
      });
    }
  }, [isUpdateRevenueSplitSuccess]);

  useEffect(() => {
    setFilteredResultsLocal([...filteredResults]);
    if (tableRef.current) tableRef.current.resetPagination();
  }, [filteredResults, onAddedCallback, onDeletedCallback]);

  const getLabel = (
    key: keyof typeof manageRevenueSplitSiteCoreKeys,
  ): string => {
    return fetchLabel(
      manageRevenueSplitSiteCoreKeys.manageRevenueSplit,
      manageRevenueSplitSiteCoreKeys[key],
    );
  };

  const handleSaveCounseling = (newValue: string) => {
    if (isNotEmpty(newValue) && showInlinePopup?.officeId) {
      updateRevenueSplitAmount({
        isForProject,
        invoiceId,
        counselingAmount: Number(valueWithoutFormat(newValue)),
        officeId: showInlinePopup.officeId,
      });
    }
  };

  const tableHeaderForm: React.ReactElement[] = [];

  const isEditable = (row: ManageRevenueSplitRow): boolean => {
    const isEditable =
      !isViewOnly &&
      row.id === MANAGE_REVENUE_SPLIT_ROW_ORDER.COUNSELLING_EDITABLE &&
      COUNSELLING_EDITABLE_RELATIONSHIP_TYPES_ARRAY.includes(
        row?.relationShipType,
      );
    return isEditable;
  };

  const handleEditClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: ManageRevenueSplitRow,
  ) => {
    setValue(
      MANAGE_REVENUE_SPLIT_INLINE_FORM_KEYS.COUNSELING,
      getValueOrBlank(row?.counselingAmountValue),
    );
    setShowInlinePopup({
      officeId: row.officeId,
      anchorEl: e.currentTarget as HTMLButtonElement,
    });
  };

  const getRegularOrBoldVariant = (rowId: number): "h3" | "h3Bold" => {
    let variant: "h3" | "h3Bold" = "h3";
    if (rowId === MANAGE_REVENUE_SPLIT_ROW_ORDER.TOTAL) {
      variant = "h3Bold";
    }
    return variant;
  };

  const renderEllipsesLabel = ({
    text,
    testId,
    rowId,
    variant,
  }: {
    text: string;
    testId: string;
    rowId: number;
    variant?: TypographyProps["variant"];
  }) => {
    return (
      <MuiEllipseLabel
        data-testid={testId}
        text={text}
        labelTypoVariant={variant ?? getRegularOrBoldVariant(rowId)}
        labelTypoClassName="width-200"
        placement="bottom-start"
      />
    );
  };

  const columns: Column[] = [
    {
      id: MANAGE_REVENUE_SPLIT_KEYS.office,
      label: getLabel("revenueSplitSectionOfficeOrAffiliatesTableColumn"),
      render: (row: RowProps) =>
        renderEllipsesLabel({
          text: row.officeAffiliate,
          testId: MANAGE_REVENUE_SPLIT_KEYS.office,
          rowId: row.id,
        }),
    },
    {
      id: MANAGE_REVENUE_SPLIT_KEYS.sales,
      label: getLabel("revenueSplitSectionSalesTableColumn"),
      render: (row: RowProps) =>
        renderEllipsesLabel({
          text: row.sales,
          testId: MANAGE_REVENUE_SPLIT_KEYS.sales,
          rowId: row.id,
        }),
    },
    {
      id: MANAGE_REVENUE_SPLIT_KEYS.counseling,
      label: getLabel("revenueSplitSectionCounselingTableColumn"),
      render: (row: RowProps) => (
        <MuiBox className="counseling-cell">
          {renderEllipsesLabel({
            text: row.counseling,
            testId: MANAGE_REVENUE_SPLIT_KEYS.counseling,
            rowId: row.id,
          })}
          {isEditable(row as ManageRevenueSplitRow) && (
            <IconButton
              size="small"
              className={isInvoiceStatusReversed ? "disabled" : ""}
              onClick={(e) => handleEditClick(e, row as ManageRevenueSplitRow)}
              aria-label="edit counseling"
              disabled={isInvoiceStatusReversed}
            >
              <EditIcon />
            </IconButton>
          )}
        </MuiBox>
      ),
    },
    {
      id: MANAGE_REVENUE_SPLIT_KEYS.affiliateRoyalty,
      label: getLabel("revenueSplitSectionAffiliateRoyaltyTableColumn"),
      render: (row: RowProps) =>
        renderEllipsesLabel({
          text: row.affiliateRoyalty,
          testId: MANAGE_REVENUE_SPLIT_KEYS.affiliateRoyalty,
          rowId: row.id,
        }),
    },
    {
      id: MANAGE_REVENUE_SPLIT_KEYS.royaltyRetention,
      label: getLabel("revenueSplitSectionRoyaltyRetentionTableColumn"),
      render: (row: RowProps) =>
        renderEllipsesLabel({
          text: row.royaltyRetention,
          testId: MANAGE_REVENUE_SPLIT_KEYS.royaltyRetention,
          rowId: row.id,
        }),
    },
    {
      id: MANAGE_REVENUE_SPLIT_KEYS.total,
      label: getLabel("revenueSplitSectionTotalTableColumn"),
      render: (row: RowProps) =>
        renderEllipsesLabel({
          text: row.total,
          testId: MANAGE_REVENUE_SPLIT_KEYS.total,
          rowId: row.id,
          variant: "h3Bold",
        }),
    },
  ];

  return (
    <ManageRevenueSplitTableWrapper>
      <MuiBox className="revenue-split-table">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={tableHeaderForm}
          columns={columns}
          tableResults={[...filteredResultsLocal]}
          noDataInApi={filteredResults.length === 0}
          noResultMessage={commonLabels.noResult}
          priorityResults={[]}
          isFooterVisible={false}
        />
      </MuiBox>

      {!isViewOnly && showInlinePopup && !!showInlinePopup.anchorEl && (
        <FormProvider {...methods}>
          <InlineEditPopup
            anchorEl={showInlinePopup.anchorEl}
            onSave={handleSaveCounseling}
            formId={MANAGE_REVENUE_SPLIT_INLINE_FORM_KEYS.COUNSELING}
            onClose={() =>
              setShowInlinePopup({
                officeId: undefined,
                anchorEl: null,
              })
            }
            label={getLabel("revenueSplitSectionCounselingTableColumn")}
            pageId={pageId}
            zeroValidationMessage={
              commonValidationMessages.greaterThanZeroFieldValidator
            }
            ignoreZeroValidation={!isInvoiceStatusReversed}
            formatErrorMessage={
              commonValidationMessages.alphabetNotAllowedValidator
            }
            currencyLocale={currencyLocale}
          />
        </FormProvider>
      )}
    </ManageRevenueSplitTableWrapper>
  );
};

export default ManageRevenueSplitTable;
